import { GoAlertFill } from "react-icons/go";
import { GrClose } from "react-icons/gr";
import * as s from "./YearlyCreditsWarningBarStyled";
import { useEffect, useMemo, useRef, useState } from "react";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  BILLING_CYCLE,
  SUBSCRIPTION_STATUS,
} from "api/interfaces/subscription.interface";
import { differenceInDays, normalizeToStartOfDay } from "utils/timeUtil";
import useUsageSignupFlow from "../signup/useUsageSignupFlow";

type ExpiredStatus =
  | SUBSCRIPTION_STATUS.EXPIRED_END
  | SUBSCRIPTION_STATUS.EXPIRED
  | SUBSCRIPTION_STATUS.NONE;

export default function YearlyCreditsWarningBar(): JSX.Element {
  const { planActiveDate }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState<boolean>(false);
  const [isBarDismissed, setIsBarDismissed] = useState<boolean>(false);
  const lastAutoOpenedKeyRef = useRef<Exclude<
    ExpiredStatus,
    SUBSCRIPTION_STATUS.NONE
  > | null>(null);

  const { state } = useUsageSignupFlow({ cycle: BILLING_CYCLE.YEARLY });

  const { paymentPrice } = state;

  const expiredList: Record<
    Exclude<ExpiredStatus, SUBSCRIPTION_STATUS.NONE>,
    { title: string; items: (string | JSX.Element)[] }
  > = {
    [SUBSCRIPTION_STATUS.EXPIRED_END]: {
      title: "Yearly Credits Exhausted",
      items: [
        "Your prepaid yearly credits have been fully consumed.",
        <>
          Service will continue and all additional channel usage will now be
          billed on a{" "}
          <strong>
            monthly usage-based plan at ${paymentPrice.channelPrice} / channel /
            month
          </strong>
        </>,
        "Invoices for overage usage will be generated at the end of each monthly period.",
      ],
    },
    [SUBSCRIPTION_STATUS.EXPIRED]: {
      title: "Your Yearly Credits Will be Exhausted in 30 days",
      items: [
        "Your prepaid yearly credits are nearly exhausted (30 days left).",
        <>
          <strong>Service will continue</strong> and all additional channel
          usage will now be billed on a{" "}
          <strong>
            monthly usage-based plan at ${paymentPrice.channelPrice} / channel /
            month
          </strong>
        </>,
        "Invoices for overage usage will be generated at the end of each monthly period.",
      ],
    },
  };

  const status = useMemo<ExpiredStatus>(() => {
    if (!planActiveDate) {
      return SUBSCRIPTION_STATUS.NONE;
    }
    const activatedAt = new Date(planActiveDate);
    if (Number.isNaN(activatedAt.getTime())) {
      return SUBSCRIPTION_STATUS.NONE;
    }

    const expiryAt = new Date(activatedAt);
    expiryAt.setFullYear(expiryAt.getFullYear() + 1);

    const today = normalizeToStartOfDay(new Date());
    const expiryDay = normalizeToStartOfDay(expiryAt);
    const daysUntilExpiry = differenceInDays(expiryDay, today);

    if (daysUntilExpiry < 0) {
      return SUBSCRIPTION_STATUS.EXPIRED_END;
    }
    if (daysUntilExpiry <= 30) {
      return SUBSCRIPTION_STATUS.EXPIRED;
    }
    return SUBSCRIPTION_STATUS.NONE;
  }, [planActiveDate]);

  useEffect(() => {
    setIsBarDismissed(false);
    if (status === SUBSCRIPTION_STATUS.NONE) {
      setIsExpiredModalOpen(false);
      lastAutoOpenedKeyRef.current = null;
      return;
    }

    if (lastAutoOpenedKeyRef.current !== status) {
      setIsExpiredModalOpen(true);
      lastAutoOpenedKeyRef.current = status;
    }
  }, [status]);

  if (
    status === SUBSCRIPTION_STATUS.NONE ||
    isBarDismissed ||
    !isExpiredModalOpen
  )
    return <></>;

  const { title, items } = expiredList[status];

  return (
    <s.Wrapper $variant={status}>
      <s.Title $variant={status}>
        <GoAlertFill />
        {title}
      </s.Title>
      <s.List>
        {items.map((item, index) => (
          <s.Item key={index}>{item}</s.Item>
        ))}
      </s.List>
      <s.CloseButton
        onClick={() => {
          setIsExpiredModalOpen(false);
          setIsBarDismissed(true);
        }}
      >
        <GrClose size={16} />
      </s.CloseButton>
    </s.Wrapper>
  );
}
